import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import Markdown, { docUrl } from './Markdown'

/**
 * Anything on the page can open this and optionally pre-ask:
 *   window.dispatchEvent(new CustomEvent('ask:open', {detail:{question}}))
 * Used by the above-the-fold AskBar. An event rather than context or a
 * store because it is one signal in one direction — see AskBar.js.
 */
export const OPEN_EVENT = 'ask:open'

/**
 * AskWidget — a question box wired to the ask-saxonberg Worker.
 *
 * ⭐ FAILS CLOSED. Without GATSBY_ASK_URL at build time this renders
 * nothing, so the site is safe to deploy before the Worker exists and
 * safe to leave deployed if the Worker is ever taken down. Set the
 * variable in GitLab → Settings → CI/CD → Variables. See worker/README.md.
 */
const ASK_URL = process.env.GATSBY_ASK_URL || ''

// Tied to what the page above actually claims, so the first click
// interrogates the pitch rather than wandering off it.
const SUGGESTIONS = [
  'What is the Compact, and how is standing earned?',
  "What's actually built versus just designed?",
  'Could I run this for my own community?',
  'What do you mean by "real units and no fudge"?',
]

/*
 * The one red action on the page. It is fixed, so it is the only thing
 * guaranteed to be on screen at every scroll position — which is exactly
 * the "single committing action per screen" the system reserves red for,
 * and why the inline CTAs gave red up for it.
 *
 * The 2px white ring is structural, not decoration: red never touches
 * blue, and this sits over the navy ground at every scroll position.
 */
const Launcher = styled.button`
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 60;
  border: 2px solid var(--white);
  border-radius: 999px;
  padding: 0.72rem 1.3rem;
  background: var(--flag-red);
  color: var(--white);
  font-family: var(--face-ui);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(2, 19, 44, 0.5);
  transition: transform 160ms ease, filter 160ms ease;

  &:hover {
    filter: brightness(1.12);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--white);
    outline-offset: 3px;
  }
  @media (max-width: 640px) {
    right: 0.75rem;
    bottom: 0.75rem;
  }
`

const Panel = styled.div`
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 61;
  width: min(30rem, calc(100vw - 1.5rem));
  max-height: min(38rem, calc(100vh - 2.5rem));
  display: flex;
  flex-direction: column;
  background: var(--ink-deep);
  border: 1px solid var(--ink-rule);
  border-radius: 12px;
  box-shadow: 0 18px 48px rgba(2, 19, 44, 0.55);
  overflow: hidden;

  @media (max-width: 640px) {
    right: 0.5rem;
    bottom: 0.5rem;
    max-height: calc(100vh - 1rem);
  }
`

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--ink-rule);
`

const Title = styled.div`
  color: var(--chalk);
  font-weight: 600;
  font-size: 0.95rem;
`

const Close = styled.button`
  background: none;
  border: none;
  color: var(--chalk-dim);
  font: inherit;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.2rem;
  &:hover {
    color: var(--chalk);
  }
`

const Log = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.9rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`

const Note = styled.p`
  margin: 0;
  color: var(--chalk-dim);
  font-size: 0.82rem;
  line-height: 1.5;
`

const Bubble = styled.div`
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: ${(p) => (p.$me ? 'pre-wrap' : 'normal')};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: ${(p) => (p.$me ? 'var(--card-ink)' : 'var(--chalk)')};
  background: ${(p) => (p.$me ? 'var(--accent)' : 'transparent')};
  border-left: ${(p) => (p.$me ? 'none' : '2px solid var(--ink-rule)')};
  border-radius: ${(p) => (p.$me ? '10px' : '0')};
  padding: ${(p) => (p.$me ? '0.5rem 0.75rem' : '0 0 0 0.75rem')};
  align-self: ${(p) => (p.$me ? 'flex-end' : 'stretch')};
  max-width: ${(p) => (p.$me ? '85%' : '100%')};
`

const Sources = styled.div`
  margin-top: 0.4rem;
  color: var(--chalk-dim);
  font-size: 0.74rem;
  word-break: break-word;
`

const SourceLink = styled.a`
  color: var(--chalk-dim);
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    color: var(--accent);
  }
`

const Err = styled(Note)`
  color: var(--flag-red-lit);
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.button`
  background: none;
  border: 1px solid var(--ink-rule);
  border-radius: 999px;
  color: var(--chalk-dim);
  font: inherit;
  font-size: 0.78rem;
  padding: 0.3rem 0.65rem;
  cursor: pointer;
  &:hover {
    color: var(--chalk);
    border-color: var(--accent);
  }
`

const Form = styled.form`
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem 0.9rem;
  border-top: 1px solid var(--ink-rule);
`

const Input = styled.input`
  flex: 1;
  min-width: 0;
  background: var(--ink);
  border: 1px solid var(--ink-rule);
  border-radius: 8px;
  color: var(--chalk);
  font: inherit;
  font-size: 0.9rem;
  padding: 0.5rem 0.65rem;
  &::placeholder {
    color: var(--chalk-dim);
  }
  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`

const Send = styled.button`
  background: var(--card);
  border: none;
  border-radius: 8px;
  color: var(--card-ink);
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const AskWidget = () => {
  const [open, setOpen] = useState(false)
  const [turns, setTurns] = useState([])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Which document the model is reading right now, for the waiting state.
  // The tool rounds are most of the latency, so naming them is the
  // difference between a dead spinner and visible progress.
  const [reading, setReading] = useState(null)
  const logRef = useRef(null)
  const inputRef = useRef(null)
  const pendingRef = useRef(null)

  const ask = async (question) => {
    const q = question.trim()
    if (!q || busy) return

    const next = [...turns, { role: 'user', content: q }]
    setTurns(next)
    setDraft('')
    setError(null)
    setBusy(true)

    setReading(null)

    try {
      const res = await fetch(ASK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          stream: true,
          messages: next.map(({ role, content }) => ({ role, content })),
        }),
      })

      // The server falls back to plain JSON if anything upstream strips
      // the stream, so honour whatever actually came back.
      const ctype = res.headers.get('Content-Type') || ''
      if (!ctype.includes('text/event-stream') || !res.body) {
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          setError(data.error || 'That did not go through. Try again shortly.')
        } else {
          setTurns([
            ...next,
            {
              role: 'assistant',
              content: data.answer,
              sources: data.sources || [],
            },
          ])
        }
        return
      }

      // Live turn: appended once, then mutated in place as deltas land.
      let text = ''
      let sources = []
      let failed = null
      setTurns([...next, { role: 'assistant', content: '', sources: [] }])

      const paint = () =>
        setTurns([
          ...next,
          { role: 'assistant', content: text, sources, streaming: true },
        ])

      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buf += dec.decode(value, { stream: true })

        let cut
        while ((cut = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, cut)
          buf = buf.slice(cut + 2)
          const line = frame.split('\n').find((l) => l.startsWith('data:'))
          if (!line) continue
          let ev
          try {
            ev = JSON.parse(line.slice(5).trim())
          } catch (_) {
            continue
          }

          if (ev.t === 'delta') {
            text += ev.text
            paint()
          } else if (ev.t === 'reset') {
            // Pre-tool narration is not the answer — see the worker.
            text = ''
            paint()
          } else if (ev.t === 'reading') {
            setReading(ev.path)
          } else if (ev.t === 'done') {
            sources = ev.sources || []
            setReading(null)
          } else if (ev.t === 'error') {
            failed = ev.error
          }
        }
      }

      if (failed) {
        setTurns(next)
        setError(failed)
      } else {
        setTurns([...next, { role: 'assistant', content: text, sources }])
      }
    } catch (e) {
      setError('Could not reach the server. It may be asleep or down.')
    } finally {
      setReading(null)
      setBusy(false)
    }
  }

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [turns, busy])

  // Opened from elsewhere on the page (the hero bar). Kept in a ref-free
  // effect with an always-registered listener so it works whether or not
  // the panel is currently open.
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true)
      const q = e && e.detail && e.detail.question
      if (q) pendingRef.current = q
    }
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  useEffect(() => {
    if (!open) return undefined
    if (inputRef.current) inputRef.current.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // A question handed in from the page opens the panel and fires
  // immediately, so the hero bar is a real entry point rather than just
  // a fancy button.
  useEffect(() => {
    if (open && pendingRef.current) {
      const q = pendingRef.current
      pendingRef.current = null
      ask(q)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // True once the assistant turn has begun filling in, so the waiting
  // note can retire the moment there is something to read.
  const last = turns[turns.length - 1]
  const streamingNow =
    !!last && last.role === 'assistant' && !!(last.content || '').trim()

  if (!ASK_URL) return null

  if (!open) {
    return (
      <Launcher onClick={() => setOpen(true)} aria-label="Ask about the project">
        Ask about the project
      </Launcher>
    )
  }

  return (
    <Panel role="dialog" aria-label="Ask about the project">
      <Head>
        <Title>Ask about the project</Title>
        <Close onClick={() => setOpen(false)} aria-label="Close">
          &times;
        </Close>
      </Head>

      <Log ref={logRef}>
        {turns.length === 0 && (
          <>
            <Note>
              This reads the project&rsquo;s published documentation and answers
              from it. It will tell you when something is designed but{' '}
              <strong>not yet built</strong> &mdash; a lot of it is.
            </Note>
            <Chips>
              {SUGGESTIONS.map((s) => (
                <Chip key={s} onClick={() => ask(s)}>
                  {s}
                </Chip>
              ))}
            </Chips>
          </>
        )}

        {turns.map((t, i) => (
          <Bubble key={i} $me={t.role === 'user'}>
            {t.role === 'user' ? t.content : <Markdown>{t.content}</Markdown>}
            {t.sources && t.sources.length > 0 && (
              <Sources>
                Sources:{' '}
                {t.sources.map((src, n) => (
                  <React.Fragment key={src}>
                    {n > 0 && ', '}
                    <SourceLink
                      href={docUrl(src)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {src.replace(/^docs\//, '')}
                    </SourceLink>
                  </React.Fragment>
                ))}
              </Sources>
            )}
          </Bubble>
        ))}

        {/* Name the document being read rather than showing a generic
            spinner: the tool rounds are most of the wait, and "reading
            parcel.md" is both true and reassuring. Once text starts
            arriving the last turn is streaming, so the note goes away. */}
        {busy && !streamingNow && (
          <Note>
            {reading ? (
              <>
                Reading <code>{reading}</code>&hellip;
              </>
            ) : (
              <>Reading the docs&hellip;</>
            )}
          </Note>
        )}
        {error && <Err>{error}</Err>}
      </Log>

      <Form
        onSubmit={(e) => {
          e.preventDefault()
          ask(draft)
        }}
      >
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask a question&hellip;"
          maxLength={2000}
          disabled={busy}
        />
        <Send type="submit" disabled={busy || !draft.trim()}>
          Ask
        </Send>
      </Form>
    </Panel>
  )
}

export default AskWidget
